import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip';
import type { ComponentProps } from 'react';
import { useTopLayerPortal } from '../../common/hooks/use-top-layer-portal';
import { mergeStyling } from '../common-functions';

const TooltipProvider = ({ delay = 0, ...props }: TooltipPrimitive.Provider.Props) => {
  return <TooltipPrimitive.Provider data-slot="tooltip-provider" delay={delay} {...props} />;
};

const Tooltip = ({ ...props }: TooltipPrimitive.Root.Props) => {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
};

const TooltipTrigger = ({ ...props }: TooltipPrimitive.Trigger.Props) => {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
};

const ArrowSvg = (props: ComponentProps<'svg'>) => {
  return (
    <svg width="20" height="10" viewBox="0 0 20 10" fill="none" aria-hidden="true" {...props}>
      <path
        d="M9.66437 2.60207L4.80758 6.97318C4.07308 7.63423 3.11989 8 2.13172 8H0V10H20V8H18.5349C17.5468 8 16.5936 7.63423 15.8591 6.97318L11.0023 2.60207C10.622 2.2598 10.0447 2.25979 9.66437 2.60207Z"
        className="fill-mono-1 dark:fill-mono-0"
      />
      <path
        d="M8.99542 1.85876C9.75604 1.17425 10.9106 1.17422 11.6713 1.85878L16.5281 6.22989C17.0789 6.72568 17.7938 7.00001 18.5349 7.00001L15.89 7L11.0023 2.60207C10.622 2.2598 10.0447 2.2598 9.66436 2.60207L4.77734 7L2.13171 7.00001C2.87284 7.00001 3.58774 6.72568 4.13861 6.22989L8.99542 1.85876Z"
        className="fill-gray-4 dark:fill-gray-1"
      />
      <path
        d="M10.3333 3.34539L5.47654 7.71648C4.55842 8.54279 3.36693 9 2.13172 9H0V8H2.13172C3.11989 8 4.07308 7.63423 4.80758 6.97318L9.66437 2.60207C10.0447 2.25979 10.622 2.2598 11.0023 2.60207L15.8591 6.97318C16.5936 7.63423 17.5468 8 18.5349 8H20V9H18.5349C17.2998 9 16.1083 8.54278 15.1901 7.71648L10.3333 3.34539Z"
        className="fill-mono-1 dark:fill-mono-0"
      />
    </svg>
  );
};

const TooltipContent = ({
  className,
  side = 'top',
  sideOffset = 10,
  align = 'center',
  alignOffset = 0,
  children,
  ...props
}: TooltipPrimitive.Popup.Props &
  Pick<TooltipPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'side' | 'sideOffset'>) => {
  const { portalAnchor, portalProps, positionerProps } = useTopLayerPortal();

  return (
    <>
      {portalAnchor}
      <TooltipPrimitive.Portal {...portalProps}>
        <TooltipPrimitive.Positioner
          {...positionerProps}
          align={align}
          alignOffset={alignOffset}
          side={side}
          sideOffset={sideOffset}
          className="isolate z-50"
        >
          <TooltipPrimitive.Popup
            data-slot="tooltip-content"
            className={mergeStyling(
              'wrap-break-word z-50 w-fit max-w-xs flex-wrap items-center gap-1.5 whitespace-normal rounded-sm px-3 py-1.5 text-xs',
              'origin-(--transform-origin) bg-mono-1 text-mono-0 dark:bg-mono-0 dark:text-mono-1',
              'shadow-sm ring-1 ring-gray-4 dark:ring-gray-1',
              'has-data-[slot=kbd]:pr-1.5',
              '**:data-[slot=kbd]:relative **:data-[slot=kbd]:isolate **:data-[slot=kbd]:z-50 **:data-[slot=kbd]:rounded-sm',
              'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
              'data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2',
              'data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2',
              'data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-[state=delayed-open]:animate-in',
              'data-open:fade-in-0 data-open:zoom-in-95 data-open:animate-in',
              'data-closed:fade-out-0 data-closed:zoom-out-95 data-closed:animate-out',
              className
            )}
            {...props}
          >
            {children}
            <TooltipPrimitive.Arrow
              data-slot="tooltip-arrow"
              className={mergeStyling(
                'z-50',
                'data-[side=top]:-bottom-2 data-[side=top]:rotate-180',
                'data-[side=bottom]:-top-2 data-[side=bottom]:rotate-0',
                'data-[side=left]:right-[-13px] data-[side=left]:rotate-90',
                'data-[side=right]:left-[-13px] data-[side=right]:-rotate-90',
                'data-[side=inline-end]:left-[-13px] data-[side=inline-end]:-rotate-90',
                'data-[side=inline-start]:right-[-13px] data-[side=inline-start]:rotate-90'
              )}
            >
              <ArrowSvg />
            </TooltipPrimitive.Arrow>
          </TooltipPrimitive.Popup>
        </TooltipPrimitive.Positioner>
      </TooltipPrimitive.Portal>
    </>
  );
};

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
